import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../auth/AuthContext';
import {
  fetchLearners,
  rememberLearner,
  rememberedLearner,
  type Learner,
} from './learners';

/**
 * Who is being worked with, in one place.
 *
 * <p>IT USED TO BE A DROPDOWN PER PAGE. Home and the week each kept their own copy of the
 * selection, read from the same key in local storage — so changing it on one page left the
 * other showing the previous learner until it happened to reload. The value has to live
 * somewhere both can watch, and a page that merely reads storage cannot be told when it moves.
 */
type Api = {
  learners: Learner[];
  learnerId: string | null;
  /** The selected learner, or null while the list is still arriving. */
  learner: Learner | null;
  /** True once the list has been asked for, so a page can tell empty from not-yet. */
  ready: boolean;
  select: (id: string) => void;
  refresh: () => Promise<void>;
};

const Ctx = createContext<Api | null>(null);

export function LearnerProvider({ children }: { children: ReactNode }) {
  const { token } = useAuth();
  const [learners, setLearners] = useState<Learner[]>([]);
  const [learnerId, setLearnerId] = useState<string | null>(rememberedLearner());
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    if (!token) {
      setLearners([]);
      setReady(false);
      return;
    }
    try {
      const list = await fetchLearners();
      setLearners(list);
      /* A REMEMBERED CHOICE THAT NO LONGER EXISTS IS NOT A CHOICE. Someone removed on another
         device would otherwise leave every page asking for a learner that is gone. */
      setLearnerId((current) => {
        const keep = current && list.some((l) => l.id === current);
        const next = keep ? current : (list[0]?.id ?? null);
        if (next) rememberLearner(next);
        return next;
      });
    } finally {
      setReady(true);
    }
  }, [token]);

  useEffect(() => { void refresh(); }, [refresh]);

  const select = useCallback((id: string) => {
    setLearnerId(id);
    rememberLearner(id);
  }, []);

  const value = useMemo<Api>(() => ({
    learners,
    learnerId,
    learner: learners.find((l) => l.id === learnerId) ?? null,
    ready,
    select,
    refresh,
  }), [learners, learnerId, ready, select, refresh]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useLearner(): Api {
  const v = useContext(Ctx);
  if (!v) throw new Error('useLearner outside LearnerProvider');
  return v;
}
